import { useEffect, useRef } from "react"

// ════════════════════════════════════════════════════════════════════
// Editable copy & data — change these to update the hero animation
// ════════════════════════════════════════════════════════════════════

export const HERO_CONTENT = {
  breadcrumb: ["Bids", "Westbrook Highway Upgrade", "Technical Response", "2. Methodology"] as const,

  // Outline sections — one avatar per section (or none)
  outline: [
    { num: 1, title: "Executive Summary", avatar: { initials: "SK", color: "#7A2E3D" }, active: false },
    { num: 2, title: "Methodology",        avatar: { initials: "MR", color: "#7B61FF" }, active: true  },
    { num: 3, title: "Project Experience", avatar: { initials: "SK", color: "#7A2E3D" }, active: false },
    { num: 4, title: "Pricing & Schedule", avatar: null,                                  active: false },
    { num: 5, title: "Compliance",         avatar: { initials: "JT", color: "#A36A0F" }, active: false },
  ] as const,

  outlineFooter: "5 sections · 2 in review",

  // Section being drafted
  sectionTitle: "2. Methodology",

  // AI panel greeting (appears above all messages)
  aiGreeting: "Tender requirements and your evidence library are loaded for this section. Ready to draft.",

  // Cycle 1: draft from scratch
  draftPrompt: "Draft this section - emphasise our zero-LTI record",
  draftSteps: [
    { title: "Reading the tender",            detail: "Section 5.2 · 800 words · 6 criteria",     duration: 850 },
    { title: "Pulling your evidence",         detail: "M5 Bridge · Northwind Rail · ISO audit",   duration: 1000 },
    { title: "Aligning with your win themes", detail: "Zero LTI · Local supply · On-time",        duration: 850 },
    { title: "Matching your house style",     detail: "Active voice · UK English",                duration: 700 },
    { title: "Drafting Methodology",          detail: "",                                         duration: 0 }, // running until stream ends
  ],

  // The drafted paragraphs (no em-dashes — reads less AI-written)
  paragraphs: [
    "We propose a fully integrated delivery model led by senior engineering oversight, with embedded SME teams from day one. Our approach pairs deep highway-construction experience, proven on the M5 Bridge Upgrade and Northwind Rail signalling renewal, with a zero-LTI safety culture independently verified through our 2023 ISO 45001 audit.",
    "The works are structured into four phases: mobilisation, earthworks and drainage, pavement and structures, and commissioning. Each phase has defined go/no-go gates. Long-lead procurement of precast units and asphalt binder is decoupled from the critical path through early-release ordering, the same approach that delivered the M5 upgrade six weeks ahead of programme.",
    "A core delivery team of 38 staff will be deployed, supplemented by our pre-vetted regional supply chain of 12 Tier-2 partners. All works above ground level are performed by our own crews; specialist signalling, pavement marking and ITS commissioning is delivered through framework partners with at least three prior projects together.",
    "A live risk register is reviewed weekly by the Project Director and reported monthly against a 14-point dashboard. We carry $25m of professional indemnity cover and operate a documented business-continuity plan, both tested on Northwind when geotechnical conditions required a 6-week design adaptation completed without programme impact.",
  ],

  citationLabel: "Northwind 2023 ISO Audit",

  // Cycle 2: select & revise (substring must exist in paragraphs[0])
  selectionText: "proven on the M5 Bridge Upgrade and Northwind Rail signalling renewal",
  revisePrompt: "Make this more specific - add the contract values",
  reviseOld: "proven on the M5 Bridge Upgrade and Northwind Rail signalling renewal",
  reviseNew: "demonstrated on the $180m M5 Bridge Upgrade and $62m Northwind Rail signalling renewal",
  reviseSteps: [
    { title: "Reading current draft",      detail: "55 words · 1 citation",          duration: 700 },
    { title: "Pulling matching evidence",  detail: "Contract values from past bids", duration: 900 },
    { title: "Applying your revision",     detail: "",                               duration: 0 },
  ],

  // Cycle 3: send for review
  reviewers: [
    { initials: "JT", color: "#A36A0F", name: "Jane Tan",     role: "Bid Manager · 2 in queue" },
    { initials: "SK", color: "#7A2E3D", name: "Sarah Kim",    role: "Senior Engineer" },
    { initials: "DH", color: "#475569", name: "Daniel Huang", role: "Project Director" },
  ] as const,
  // Index of reviewer that auto-selects in the demo
  autoReviewerIdx: 0,

  // Toast strings
  toasts: {
    draftReady: "Draft ready · 1 citation",
    diffPending: "Suggested edit · Accept or Reject",
    reviewSent: "Sent to Jane Tan for review",
  },

  // Status pill labels
  status: {
    empty:      { text: "Empty",         kind: "empty" as const },
    drafting:   { text: "Drafting…",     kind: "in-progress" as const },
    inProgress: { text: "In progress",   kind: "in-progress" as const },
    inReview:   { text: "In review · JT", kind: "review" as const },
  },
}

// ════════════════════════════════════════════════════════════════════
// Animation timings — tweak these to change pacing (in ms)
// ════════════════════════════════════════════════════════════════════

export const TIMINGS = {
  // Cycle 1
  startDelay: 700,                  // before typing the first prompt
  promptCharDelay: 32,              // how fast prompts type
  promptToBubbleDelay: 350,         // pause between input cleared & bubble shown
  bubbleToStepsDelay: 400,
  paragraphFirstDelay: 4,           // all paragraphs stream at the same fast pace
  paragraphFastDelay: 4,
  paragraphPauseBetween: 200,
  citationAppearDelay: 200,
  toastAfterCompleteDelay: 350,
  cycle1HoldMs: 1500,

  // Cycle 2
  selectionAppearDelay: 200,
  selectionChipDelay: 250,
  revisePromptStartDelay: 600,
  reviseStepsStartDelay: 350,
  diffActionsAfterStepsDelay: 350,
  diffToastDelay: 600,
  cycle2HoldMs: 1500,

  // Cycle 3
  acceptAtStartDelay: 400,
  dropdownOpenDelay: 400,
  dropdownHighlightDelay: 600,
  dropdownCloseDelay: 500,
  reviewToastDelay: 250,
  cycle3HoldMs: 4000,
}

// ════════════════════════════════════════════════════════════════════
// Component
// ════════════════════════════════════════════════════════════════════

const CHEV_RIGHT = (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
)

// Inline starburst — matches BidContextLogo, but at a smaller stroke for inline use
function Burst({ size = 10, className }: { size?: number; className?: string }) {
  const ROTS = [0, 45, 90, 135, 180, 225, 270, 315]
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" className={className} aria-hidden="true">
      <g stroke="currentColor" strokeWidth="11" strokeLinecap="round">
        {ROTS.map((deg) => (
          <line key={deg} x1="50" y1="35" x2="50" y2="13" transform={`rotate(${deg} 50 50)`} />
        ))}
      </g>
    </svg>
  )
}

export function HeroProductWindow() {
  // Refs to all DOM nodes the animation drives
  const rootRef = useRef<HTMLDivElement>(null)
  const promptRef = useRef<HTMLSpanElement>(null)
  const userMsgRef = useRef<HTMLDivElement>(null)
  const userMsgTextRef = useRef<HTMLSpanElement>(null)
  const activityRef = useRef<HTMLDivElement>(null)
  const activitySummaryRef = useRef<HTMLSpanElement>(null)
  const activityIconRef = useRef<HTMLSpanElement>(null)
  const stepListRef = useRef<HTMLDivElement>(null)
  const para1Ref = useRef<HTMLParagraphElement>(null)
  const para2Ref = useRef<HTMLParagraphElement>(null)
  const para3Ref = useRef<HTMLParagraphElement>(null)
  const para4Ref = useRef<HTMLParagraphElement>(null)
  const citationRef = useRef<HTMLDivElement>(null)
  const toastRef = useRef<HTMLDivElement>(null)
  const toastTextRef = useRef<HTMLSpanElement>(null)
  const emptySkelRef = useRef<HTMLDivElement>(null)
  const wordCountRef = useRef<HTMLSpanElement>(null)
  const citeCountRef = useRef<HTMLSpanElement>(null)
  const statusPillRef = useRef<HTMLSpanElement>(null)
  const selectionChipRef = useRef<HTMLDivElement>(null)
  const diffActionsRef = useRef<HTMLDivElement>(null)
  const reviewDropdownRef = useRef<HTMLDivElement>(null)
  const sidebarMethodAvatarRef = useRef<HTMLSpanElement>(null)
  const editorActionsRef = useRef<HTMLDivElement>(null)
  const sendReviewBtnRef = useRef<HTMLButtonElement>(null)
  const revOpt1Ref = useRef<HTMLButtonElement>(null)
  const editorScrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    let timers: ReturnType<typeof setTimeout>[] = []
    let isPlaying = false
    let isInView = false

    const tt = (fn: () => void, ms: number) => {
      const t = setTimeout(fn, ms)
      timers.push(t)
      return t
    }
    const clearTimers = () => {
      timers.forEach((t) => clearTimeout(t))
      timers = []
    }

    // Helpers ─────────────────────────
    const paraEls = [para1Ref.current, para2Ref.current, para3Ref.current, para4Ref.current].filter(Boolean) as HTMLElement[]

    function setStatus(text: string, kind: "empty" | "in-progress" | "review") {
      const el = statusPillRef.current
      if (!el) return
      el.textContent = text
      if (kind === "in-progress") {
        el.style.background = "var(--lp-primary-soft)"
        el.style.color = "var(--lp-primary)"
      } else if (kind === "review") {
        el.style.background = "#FEF3C7"
        el.style.color = "#A16207"
      } else {
        el.style.background = "#F2F4F7"
        el.style.color = "var(--lp-muted)"
      }
    }

    function setSidebarAvatar(initials: string, color: string) {
      const el = sidebarMethodAvatarRef.current
      if (!el) return
      el.textContent = initials
      el.style.background = color
    }

    function getAllStreamedText() {
      return paraEls.map((p) => p.textContent ?? "").join(" ")
    }

    function autoScroll() {
      const el = editorScrollRef.current
      if (el) el.scrollTop = el.scrollHeight
    }

    function reset() {
      if (promptRef.current) {
        promptRef.current.textContent = ""
        promptRef.current.classList.add("hero-blink-caret")
      }
      userMsgRef.current?.classList.remove("show")
      if (userMsgTextRef.current) userMsgTextRef.current.textContent = ""
      activityRef.current?.classList.add("hidden")
      if (stepListRef.current) stepListRef.current.innerHTML = ""
      paraEls.forEach((p) => { p.innerHTML = "" })
      citationRef.current?.classList.remove("show")
      toastRef.current?.classList.remove("show")
      selectionChipRef.current?.classList.remove("show")
      diffActionsRef.current?.classList.remove("show")
      reviewDropdownRef.current?.classList.remove("show")
      editorActionsRef.current?.classList.remove("show")
      if (emptySkelRef.current) emptySkelRef.current.style.display = ""
      if (wordCountRef.current) wordCountRef.current.textContent = "0 / 800 words"
      if (citeCountRef.current) citeCountRef.current.textContent = "0 citations"
      setStatus("Empty", "empty")
      setSidebarAvatar("MR", "#7B61FF")
      if (sendReviewBtnRef.current) sendReviewBtnRef.current.style.outline = ""
      if (activityIconRef.current) {
        activityIconRef.current.innerHTML =
          '<svg width="10" height="10" viewBox="0 0 100 100" fill="none" class="hero-breathe" style="color:var(--hero-ai-accent)"><g stroke="currentColor" stroke-width="11" stroke-linecap="round"><line x1="50" y1="35" x2="50" y2="13"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(45 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(90 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(135 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(180 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(225 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(270 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(315 50 50)"/></g></svg>'
      }
    }

    function typeInto(el: HTMLElement, text: string, charDelay: number, onDone?: () => void) {
      let i = 0
      function step() {
        if (!isPlaying) return
        if (i < text.length) {
          el.textContent = text.slice(0, i + 1)
          i++
          tt(step, charDelay)
        } else if (onDone) onDone()
      }
      step()
    }

    function streamParagraphs(firstDelay: number, fastDelay: number, paragraphPause: number, onDone: () => void) {
      let pIdx = 0
      function startNext() {
        if (!isPlaying) return
        if (pIdx >= HERO_CONTENT.paragraphs.length) { onDone(); return }
        const target: HTMLElement | undefined = paraEls[pIdx]
        const text: string | undefined = HERO_CONTENT.paragraphs[pIdx]
        if (!target || !text) { onDone(); return }
        const safeTarget: HTMLElement = target
        const safeText: string = text
        const charDelay = pIdx === 0 ? firstDelay : fastDelay
        let cIdx = 0
        function streamChar() {
          if (!isPlaying) return
          if (cIdx < safeText.length) {
            safeTarget.textContent = safeText.slice(0, cIdx + 1)
            const words = getAllStreamedText().trim().split(/\s+/).filter(Boolean).length
            if (wordCountRef.current) wordCountRef.current.textContent = `${words} / 800 words`
            autoScroll()
            cIdx++
            tt(streamChar, charDelay)
          } else {
            pIdx++
            tt(startNext, paragraphPause)
          }
        }
        streamChar()
      }
      startNext()
    }

    function renderStep(step: { title: string; detail: string }, status: "running" | "completed") {
      if (!stepListRef.current) return null
      const row = document.createElement("div")
      row.className = "hero-step-row flex items-start gap-2 text-[11px]"
      const burstSvg =
        '<svg width="10" height="10" viewBox="0 0 100 100" fill="none" class="hero-breathe"><g stroke="currentColor" stroke-width="11" stroke-linecap="round"><line x1="50" y1="35" x2="50" y2="13"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(45 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(90 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(135 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(180 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(225 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(270 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(315 50 50)"/></g></svg>'
      const checkSvg =
        '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>'
      const iconHTML = status === "running"
        ? `<span class="mt-[3px] shrink-0" style="color:var(--hero-ai-accent)">${burstSvg}</span>`
        : `<span class="mt-[3px] shrink-0" style="color:var(--lp-muted)">${checkSvg}</span>`
      const detailHTML = step.detail
        ? `<div class="text-[10px] mt-0.5" style="color:var(--lp-muted); opacity:${status === "running" ? "0.85" : "0.7"}">${step.detail}</div>`
        : ""
      row.innerHTML = `
        ${iconHTML}
        <div class="min-w-0 flex-1">
          <div style="color:${status === "running" ? "var(--lp-fg)" : "var(--lp-muted)"}; font-weight:${status === "running" ? "500" : "400"}">${step.title}</div>
          ${detailHTML}
        </div>
      `
      stepListRef.current.appendChild(row)
      requestAnimationFrame(() => row.classList.add("show"))
      return row
    }

    function completeStep(row: HTMLDivElement | null, step: { title: string; detail: string }) {
      if (!row) return
      const checkSvg =
        '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>'
      const iconHTML = `<span class="mt-[3px] shrink-0" style="color:var(--lp-muted)">${checkSvg}</span>`
      const detailHTML = step.detail
        ? `<div class="text-[10px] mt-0.5" style="color:var(--lp-muted); opacity:0.7">${step.detail}</div>`
        : ""
      row.innerHTML = `
        ${iconHTML}
        <div class="min-w-0 flex-1">
          <div style="color:var(--lp-muted)">${step.title}</div>
          ${detailHTML}
        </div>
      `
    }

    function runSteps(stepsArr: typeof HERO_CONTENT.draftSteps, idx: number, onAllDone: (lastRow: HTMLDivElement | null) => void) {
      if (!isPlaying) return
      if (idx >= stepsArr.length) { onAllDone(null); return }
      const step = stepsArr[idx]
      if (!step) { onAllDone(null); return }
      const row = renderStep(step, "running")
      if (activitySummaryRef.current) activitySummaryRef.current.textContent = `${idx + 1} of ${stepsArr.length} · ${step.title}`
      if (step.duration === 0) {
        onAllDone(row as HTMLDivElement)
        return
      }
      tt(() => {
        completeStep(row as HTMLDivElement, step)
        runSteps(stepsArr, idx + 1, onAllDone)
      }, step.duration)
    }

    function clearStepsCard() {
      if (stepListRef.current) stepListRef.current.innerHTML = ""
      activityRef.current?.classList.add("hidden")
      if (activityIconRef.current) {
        activityIconRef.current.innerHTML =
          '<svg width="10" height="10" viewBox="0 0 100 100" fill="none" class="hero-breathe" style="color:var(--hero-ai-accent)"><g stroke="currentColor" stroke-width="11" stroke-linecap="round"><line x1="50" y1="35" x2="50" y2="13"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(45 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(90 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(135 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(180 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(225 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(270 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(315 50 50)"/></g></svg>'
      }
    }

    // ═══════════════════════════════ Cycle 1 — Draft ═══════════════════════════════
    function cycleDraft(onDone: () => void) {
      tt(() => {
        if (!promptRef.current) return
        promptRef.current.classList.remove("hero-blink-caret")
        typeInto(promptRef.current, HERO_CONTENT.draftPrompt, TIMINGS.promptCharDelay, () => {
          tt(() => {
            if (userMsgTextRef.current) userMsgTextRef.current.textContent = HERO_CONTENT.draftPrompt
            userMsgRef.current?.classList.add("show")
            if (promptRef.current) {
              promptRef.current.textContent = ""
              promptRef.current.classList.add("hero-blink-caret")
            }
            setStatus(HERO_CONTENT.status.drafting.text, "in-progress")

            tt(() => {
              activityRef.current?.classList.remove("hidden")
              runSteps(HERO_CONTENT.draftSteps, 0, (draftingRow) => {
                if (emptySkelRef.current) emptySkelRef.current.style.display = "none"
                streamParagraphs(TIMINGS.paragraphFirstDelay, TIMINGS.paragraphFastDelay, TIMINGS.paragraphPauseBetween, () => {
                  tt(() => {
                    citationRef.current?.classList.add("show")
                    autoScroll()
                  }, TIMINGS.citationAppearDelay)
                  tt(() => {
                    const finalWords = getAllStreamedText().trim().split(/\s+/).filter(Boolean).length
                    if (draftingRow) completeStep(draftingRow, { title: "Drafting Methodology", detail: `${finalWords} words · 1 citation` })
                    if (activitySummaryRef.current) activitySummaryRef.current.textContent = `${HERO_CONTENT.draftSteps.length} steps completed`
                    if (activityIconRef.current) {
                      activityIconRef.current.innerHTML = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="color:var(--lp-primary)"><polyline points="20 6 9 17 4 12"/></svg>'
                    }
                    if (citeCountRef.current) citeCountRef.current.textContent = "1 citation"
                    setStatus(HERO_CONTENT.status.inProgress.text, "in-progress")
                    if (toastTextRef.current) toastTextRef.current.textContent = HERO_CONTENT.toasts.draftReady
                    toastRef.current?.classList.add("show")
                    editorActionsRef.current?.classList.add("show")
                    tt(onDone, TIMINGS.cycle1HoldMs)
                  }, TIMINGS.toastAfterCompleteDelay)
                })
              })
            }, TIMINGS.bubbleToStepsDelay)
          }, TIMINGS.promptToBubbleDelay)
        })
      }, TIMINGS.startDelay)
    }

    // ═══════════════════════════════ Cycle 2 — Revise with diff ═══════════════════════════════
    function cycleRevise(onDone: () => void) {
      tt(() => {
        const para1 = paraEls[0]
        if (!para1) { onDone(); return }
        const html = para1.innerHTML
        const selStart = html.indexOf(HERO_CONTENT.selectionText)
        if (selStart === -1) { onDone(); return }
        const before = html.slice(0, selStart)
        const after = html.slice(selStart + HERO_CONTENT.selectionText.length)
        para1.innerHTML = `${before}<span id="hero-sel-span" class="hero-select-highlight">${HERO_CONTENT.selectionText}</span>${after}`

        const sel = document.getElementById("hero-sel-span")
        const scrollEl = editorScrollRef.current
        if (sel && scrollEl) {
          const selRect = sel.getBoundingClientRect()
          const scrollRect = scrollEl.getBoundingClientRect()
          const offsetWithin = selRect.top - scrollRect.top + scrollEl.scrollTop
          const target = Math.max(0, offsetWithin - scrollEl.clientHeight / 3)
          scrollEl.scrollTo({ top: target, behavior: "smooth" })
        }

        tt(() => selectionChipRef.current?.classList.add("show"), TIMINGS.selectionChipDelay)

        tt(() => {
          if (!promptRef.current) return
          promptRef.current.classList.remove("hero-blink-caret")
          typeInto(promptRef.current, HERO_CONTENT.revisePrompt, TIMINGS.promptCharDelay, () => {
            tt(() => {
              if (userMsgTextRef.current) userMsgTextRef.current.textContent = HERO_CONTENT.revisePrompt
              if (promptRef.current) {
                promptRef.current.textContent = ""
                promptRef.current.classList.add("hero-blink-caret")
              }
              toastRef.current?.classList.remove("show")
              clearStepsCard()

              const selSpan = document.getElementById("hero-sel-span")
              if (selSpan) selSpan.classList.add("fade")

              tt(() => {
                activityRef.current?.classList.remove("hidden")
                runSteps(HERO_CONTENT.reviseSteps, 0, (revRow) => {
                  if (selSpan) {
                    selSpan.outerHTML = `<span class="hero-diff-del">${HERO_CONTENT.reviseOld}</span><span class="hero-diff-add">${HERO_CONTENT.reviseNew}</span>`
                  }
                  if (revRow) completeStep(revRow, { title: "Applying your revision", detail: "Replaced 1 phrase" })
                  if (activitySummaryRef.current) activitySummaryRef.current.textContent = `${HERO_CONTENT.reviseSteps.length} steps completed`
                  if (activityIconRef.current) {
                    activityIconRef.current.innerHTML = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="color:var(--lp-primary)"><polyline points="20 6 9 17 4 12"/></svg>'
                  }

                  tt(() => {
                    diffActionsRef.current?.classList.add("show")
                    const sEl = editorScrollRef.current
                    const da = diffActionsRef.current
                    if (sEl && da) {
                      const r = da.getBoundingClientRect()
                      const sr = sEl.getBoundingClientRect()
                      const offset = r.top - sr.top + sEl.scrollTop
                      const target = Math.max(0, offset - sEl.clientHeight / 3)
                      sEl.scrollTo({ top: target, behavior: "smooth" })
                    }
                  }, TIMINGS.diffActionsAfterStepsDelay)

                  tt(() => {
                    if (toastTextRef.current) toastTextRef.current.textContent = HERO_CONTENT.toasts.diffPending
                    toastRef.current?.classList.add("show")
                    tt(onDone, TIMINGS.cycle2HoldMs)
                  }, TIMINGS.diffToastDelay)
                })
              }, TIMINGS.reviseStepsStartDelay)
            }, TIMINGS.promptToBubbleDelay)
          })
        }, TIMINGS.revisePromptStartDelay)
      }, TIMINGS.selectionAppearDelay)
    }

    // ═══════════════════════════════ Cycle 3 — Send for review ═══════════════════════════════
    function cycleSendForReview(onDone: () => void) {
      tt(() => {
        const para1 = paraEls[0]
        if (para1) {
          const html = para1.innerHTML
          const resolved = html.replace(
            /<span class="hero-diff-del">[^<]+<\/span><span class="hero-diff-add">([^<]+)<\/span>/,
            '<span class="hero-diff-add-flash">$1</span>'
          )
          para1.innerHTML = resolved
        }
        diffActionsRef.current?.classList.remove("show")
        // Once accepted, the selection no longer applies — hide the selection chip in the AI panel
        selectionChipRef.current?.classList.remove("show")
        const words = getAllStreamedText().trim().split(/\s+/).filter(Boolean).length
        if (wordCountRef.current) wordCountRef.current.textContent = `${words} / 800 words`
        toastRef.current?.classList.remove("show")

        tt(() => {
          if (sendReviewBtnRef.current) sendReviewBtnRef.current.style.outline = "2px solid var(--lp-primary-soft)"
          reviewDropdownRef.current?.classList.add("show")

          tt(() => {
            if (revOpt1Ref.current) revOpt1Ref.current.style.background = "var(--lp-primary-soft)"
            tt(() => {
              reviewDropdownRef.current?.classList.remove("show")
              if (sendReviewBtnRef.current) sendReviewBtnRef.current.style.outline = ""
              if (revOpt1Ref.current) revOpt1Ref.current.style.background = ""
              setStatus(HERO_CONTENT.status.inReview.text, "review")
              setSidebarAvatar("JT", "#A36A0F")

              tt(() => {
                if (toastTextRef.current) toastTextRef.current.textContent = HERO_CONTENT.toasts.reviewSent
                toastRef.current?.classList.add("show")
                tt(onDone, TIMINGS.cycle3HoldMs)
              }, TIMINGS.reviewToastDelay)
            }, TIMINGS.dropdownCloseDelay)
          }, TIMINGS.dropdownHighlightDelay)
        }, TIMINGS.dropdownOpenDelay)
      }, TIMINGS.acceptAtStartDelay)
    }

    function play() {
      if (isPlaying) return
      isPlaying = true
      reset()

      if (reduced) {
        if (userMsgTextRef.current) userMsgTextRef.current.textContent = HERO_CONTENT.revisePrompt
        userMsgRef.current?.classList.add("show")
        paraEls.forEach((p, i) => {
          const raw = HERO_CONTENT.paragraphs[i] ?? ""
          const text = i === 0 ? raw.replace(HERO_CONTENT.reviseOld, HERO_CONTENT.reviseNew) : raw
          p.textContent = text
        })
        citationRef.current?.classList.add("show")
        if (emptySkelRef.current) emptySkelRef.current.style.display = "none"
        if (toastTextRef.current) toastTextRef.current.textContent = HERO_CONTENT.toasts.reviewSent
        toastRef.current?.classList.add("show")
        const w = getAllStreamedText().trim().split(/\s+/).filter(Boolean).length
        if (wordCountRef.current) wordCountRef.current.textContent = `${w} / 800 words`
        if (citeCountRef.current) citeCountRef.current.textContent = "1 citation"
        setStatus(HERO_CONTENT.status.inReview.text, "review")
        setSidebarAvatar("JT", "#A36A0F")
        editorActionsRef.current?.classList.add("show")
        return
      }

      cycleDraft(() => {
        if (!isPlaying) return
        cycleRevise(() => {
          if (!isPlaying) return
          cycleSendForReview(() => {
            isPlaying = false
            if (isInView) play()
          })
        })
      })
    }

    function stop() {
      isPlaying = false
      clearTimers()
    }

    // Only auto-play when the full card is in view.
    // Multiple thresholds give us fine-grained intersectionRatio updates;
    // we then compare against a target ratio that adapts to the viewport
    // so the animation can still trigger on shorter viewports where
    // 100% visibility is impossible.
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const cardH = (e.target as HTMLElement).offsetHeight
          const vpH = window.innerHeight
          // If card fits in viewport, require ~95% visible.
          // If card is taller than viewport, require viewport-fill ratio
          // (i.e. card occupies as much of the viewport as it possibly can).
          const requiredRatio = cardH > vpH
            ? Math.max(0.6, (vpH / cardH) * 0.95)
            : 0.95
          const wasInView = isInView
          isInView = e.intersectionRatio >= requiredRatio
          if (isInView && !wasInView) {
            if (!isPlaying) play()
          } else if (!isInView && wasInView) {
            stop()
          }
        })
      },
      { threshold: [0, 0.25, 0.5, 0.75, 0.85, 0.95, 1.0] }
    )
    io.observe(root)

    return () => {
      io.disconnect()
      stop()
    }
  }, [])

  // ═══════════════ Render ═══════════════
  return (
    <div ref={rootRef} className="hero-window-frame relative">
      {/* Slim breadcrumb */}
      <header className="px-5 h-9 flex items-center border-b lp-hairline" style={{ background: "var(--lp-mock-surface)" }}>
        <nav className="flex items-center gap-2 text-[12px] min-w-0" style={{ color: "var(--lp-muted)" }}>
          {HERO_CONTENT.breadcrumb.map((seg, i) => {
            const isLast = i === HERO_CONTENT.breadcrumb.length - 1
            return (
              <span key={i} className="flex items-center gap-2 min-w-0">
                <span
                  className={`truncate ${isLast ? "font-medium" : ""}`}
                  style={isLast ? { color: "var(--lp-fg)" } : undefined}
                >
                  {seg}
                </span>
                {!isLast && CHEV_RIGHT}
              </span>
            )
          })}
        </nav>
      </header>

      <div className="grid" style={{ gridTemplateColumns: "200px 1fr 320px", height: "560px" }}>
        {/* ── Sidebar ── */}
        <aside className="border-r lp-hairline p-3 text-sm flex flex-col" style={{ background: "var(--lp-mock-surface)" }}>
          <div className="text-[10px] uppercase tracking-wider mb-2 px-2" style={{ color: "var(--lp-muted)" }}>Outline</div>
          {HERO_CONTENT.outline.map((s) => (
            <button
              key={s.num}
              className={`w-full px-2 py-2 rounded text-[12px] flex items-center justify-between ${s.active ? "font-medium" : "hover:bg-white/50"}`}
              style={s.active
                ? { color: "var(--lp-primary)" }
                : { color: "var(--lp-muted)" }}
            >
              <span className="truncate">{s.num}. {s.title}</span>
              {s.avatar && (
                <span
                  ref={s.active ? sidebarMethodAvatarRef : undefined}
                  className="hero-avatar hero-avatar-sm shrink-0"
                  style={{ background: s.avatar.color }}
                >{s.avatar.initials}</span>
              )}
            </button>
          ))}
          <div className="mt-auto h-9 -mx-3 -mb-3 px-5 flex items-center border-t lp-hairline text-[10px]" style={{ color: "var(--lp-muted)", background: "var(--lp-mock-surface)" }}>
            {HERO_CONTENT.outlineFooter}
          </div>
        </aside>

        {/* ── Editor ── */}
        <main className="flex flex-col min-h-0 overflow-hidden">
          <header className="h-12 px-6 flex items-center justify-between border-b lp-hairline shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <h2 className="text-[14px] font-semibold truncate">{HERO_CONTENT.sectionTitle}</h2>
              <span ref={statusPillRef} className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: "#F2F4F7", color: "var(--lp-muted)" }}>Empty</span>
            </div>
            <div ref={editorActionsRef} className="hero-editor-actions flex items-center gap-2 relative">
              <button className="px-2.5 h-7 text-[11px] font-medium rounded border lp-hairline" style={{ color: "var(--lp-fg)", background: "var(--lp-card)" }}>
                Mark complete
              </button>
              <button ref={sendReviewBtnRef} className="px-2.5 h-7 text-[11px] font-medium rounded border lp-hairline inline-flex items-center gap-1.5" style={{ color: "var(--lp-fg)", background: "var(--lp-card)" }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
                Send for Review
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
              </button>

              <div ref={reviewDropdownRef} className="hero-review-dropdown absolute right-0 top-full mt-1.5 w-56 rounded-lg overflow-hidden z-10" style={{ background: "var(--lp-card)", border: "1px solid var(--lp-border)", boxShadow: "0 10px 30px -8px rgba(0,0,0,0.15)" }}>
                <div className="px-3 py-2 text-[10px] uppercase tracking-wider border-b lp-hairline" style={{ color: "var(--lp-muted)", background: "var(--lp-mock-surface)" }}>
                  Send to reviewer
                </div>
                {HERO_CONTENT.reviewers.map((r, i) => (
                  <button
                    key={r.initials}
                    ref={i === HERO_CONTENT.autoReviewerIdx ? revOpt1Ref : undefined}
                    className="w-full px-3 py-2 flex items-center gap-2 text-[12px] hover:bg-[var(--lp-mock-surface)]"
                    style={{ color: "var(--lp-fg)" }}
                  >
                    <span className="hero-avatar hero-avatar-sm" style={{ background: r.color }}>{r.initials}</span>
                    <div className="flex-1 text-left min-w-0">
                      <div className="font-medium truncate">{r.name}</div>
                      <div className="text-[10px]" style={{ color: "var(--lp-muted)" }}>{r.role}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </header>

          {/* Editor body */}
          <div ref={editorScrollRef} className="flex-1 min-h-0 px-9 pt-4 pb-7 overflow-y-auto hero-thin-scroll" style={{ background: "var(--lp-card)" }}>
            <div className="text-[12.5px] leading-[1.7]" style={{ color: "var(--lp-fg)" }}>
              <p ref={para1Ref} className="mb-3" />
              <div ref={diffActionsRef} className="hero-diff-actions flex w-fit items-center gap-1.5 px-2 py-1 rounded-md border lp-hairline" style={{ background: "var(--lp-card)", boxShadow: "0 2px 8px -2px rgba(0,0,0,0.08)" }}>
                <span className="text-[10px]" style={{ color: "var(--lp-muted)" }}>Suggested edit</span>
                <span className="w-px h-3" style={{ background: "var(--lp-border)" }} />
                <button className="text-[11px] font-medium px-2 py-0.5 rounded inline-flex items-center gap-1" style={{ color: "#1F5C3D" }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  Accept
                </button>
                <button className="text-[11px] px-2 py-0.5 rounded" style={{ color: "var(--lp-muted)" }}>Reject</button>
              </div>
              <p ref={para2Ref} className="mb-3" />
              <p ref={para3Ref} className="mb-3" />
              <p ref={para4Ref} />
              <div ref={citationRef} className="hero-citation mt-1">
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                {HERO_CONTENT.citationLabel}
              </div>
            </div>
            <div ref={emptySkelRef} className="space-y-2.5">
              <div className="hero-skel w-full" />
              <div className="hero-skel w-[94%]" />
              <div className="hero-skel w-[88%]" />
              <div className="hero-skel w-[60%]" />
              <div className="hero-skel w-full mt-5" />
              <div className="hero-skel w-[82%]" />
              <div className="hero-skel w-[40%]" />
            </div>
          </div>

          {/* Editor footer */}
          <footer className="h-9 px-6 flex items-center justify-between border-t lp-hairline shrink-0 text-[10px]" style={{ background: "var(--lp-mock-surface)", color: "var(--lp-muted)" }}>
            <div className="flex items-center gap-3">
              <span ref={wordCountRef}>0 / 800 words</span>
              <span>·</span>
              <span ref={citeCountRef}>0 citations</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#5BA77C" }} />
              <span>Saved · just now</span>
            </div>
          </footer>
        </main>

        {/* ── AI Panel ── */}
        <aside className="border-l lp-hairline flex flex-col min-h-0 overflow-hidden" style={{ background: "var(--lp-card)" }}>
          <div className="h-12 px-2 flex items-center justify-center border-b lp-hairline shrink-0">
            <button className="px-3 py-2.5 text-[12px] font-medium border-b-2" style={{ borderColor: "var(--lp-primary)", color: "var(--lp-fg)", marginBottom: "-1px" }}>Write</button>
            <button className="px-3 py-2.5 text-[12px]" style={{ color: "var(--lp-muted)" }}>Review</button>
            <button className="px-3 py-2.5 text-[12px]" style={{ color: "var(--lp-muted)" }}>Comments</button>
          </div>

          <div className="px-4 py-2 border-b lp-hairline shrink-0 flex items-center gap-2 text-[11px]" style={{ background: "var(--lp-mock-surface)", color: "var(--lp-muted)" }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <span className="font-medium" style={{ color: "var(--lp-fg)" }}>{HERO_CONTENT.sectionTitle}</span>
          </div>

          <div className="flex-1 px-4 py-4 flex flex-col gap-2.5 overflow-hidden">
            {/* Greeting */}
            <div className="flex items-start gap-2">
              <div className="shrink-0 mt-0.5" style={{ color: "var(--lp-primary)" }}>
                <Burst size={14} />
              </div>
              <div className="text-[12px] leading-[1.45]" style={{ color: "var(--lp-muted)" }}>
                {HERO_CONTENT.aiGreeting}
              </div>
            </div>

            <div ref={userMsgRef} className="hero-ai-msg-user">
              <span ref={userMsgTextRef} />
            </div>

            <div ref={activityRef} className="hidden mt-1 ml-7 rounded-lg px-3 py-2 space-y-1.5" style={{ background: "var(--lp-mock-surface)", border: "1px solid var(--lp-border)" }}>
              <div className="flex items-center gap-1.5 text-[11px] mb-1" style={{ color: "var(--lp-muted)" }}>
                <span ref={activityIconRef} style={{ color: "var(--hero-ai-accent)" }}>
                  <Burst size={10} className="hero-breathe" />
                </span>
                <span ref={activitySummaryRef} className="font-medium">Working…</span>
              </div>
              <div ref={stepListRef} className="space-y-1 ml-1 pl-3 border-l lp-hairline" />
            </div>

            <div ref={toastRef} className="hero-toast ml-7 px-3 py-2 rounded-lg flex items-center gap-2 text-[11px]" style={{ background: "var(--lp-card)", border: "1px solid var(--lp-border)", boxShadow: "0 4px 14px -4px rgba(0,0,0,0.06)" }}>
              <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: "var(--lp-primary)" }}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <span ref={toastTextRef} className="font-medium">{HERO_CONTENT.toasts.draftReady}</span>
              <button className="ml-auto text-[10px]" style={{ color: "var(--lp-muted)" }}>Undo</button>
            </div>

            <div ref={selectionChipRef} className="hero-toast ml-7 px-3 py-2 rounded-lg flex items-center gap-2 text-[11px]" style={{ background: "var(--lp-mock-surface)", border: "1px dashed var(--lp-border)" }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: "var(--lp-muted)" }}><path d="M16 3h5v5M21 3l-7 7M14 21H4a1 1 0 0 1-1-1V10"/></svg>
              <span style={{ color: "var(--lp-muted)" }}>Selected:</span>
              <span className="font-medium truncate" style={{ color: "var(--lp-fg)" }}>{`"… M5 Bridge Upgrade and Northwind Rail …"`}</span>
            </div>

            <div className="flex-1" />
          </div>

          <div className="px-4 pb-3 shrink-0">
            <div className="border lp-hairline rounded-lg px-3 py-2 flex items-start gap-2" style={{ background: "var(--lp-mock-surface)" }}>
              <span ref={promptRef} className="text-[12px] leading-[1.4] flex-1 min-h-[18px] hero-blink-caret" style={{ color: "var(--lp-fg)" }} />
              <button className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--lp-primary)" }} aria-label="Send">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
              </button>
            </div>
            <div className="flex items-center gap-1.5 mt-1.5">
              <button className="w-6 h-6 rounded-md border lp-hairline inline-flex items-center justify-center" style={{ color: "var(--lp-muted)", background: "var(--lp-card)" }} title="Attach">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
              </button>
              <button className="px-2 h-6 text-[10px] rounded-md border lp-hairline inline-flex items-center gap-1" style={{ color: "var(--lp-muted)", background: "var(--lp-card)" }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="13" x2="12" y2="17"/>
                  <line x1="10" y1="15" x2="14" y2="15"/>
                </svg>
                Add evidence
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}
